import useEventListener from '../../hooks/useEventListener.hooks';

/**
 * HITL-1499: `Alt+I` files an issue report. Cloned deliberately from
 * `useIntentHotkeys` — same modifier guard, same `event.code` match, same
 * window-capture listener — because that shape buys three properties, none of
 * them optional:
 *
 * 1. **The interception dialog never sees the keydown.** AgentInterceptionConfirm
 *    listens on `document` in the capture phase; window capture is strictly
 *    outside document capture, so `stopPropagation()` here stops it.
 *
 *    Deliberate HERE and only here: flagging a defect is supervisory. `Alt+1…9`
 *    used to share this and should not have — firing an intent speaks to the
 *    customer, so it must prompt. `useIntentHotkeys` no longer stops propagation.
 * 2. **It coexists with the other window-capture listeners.** `useIntentHotkeys`,
 *    PulseAdvise (`Alt+A`/`Alt+D`), KeyboardShortcutsHelp (`Alt+/`) and
 *    AutocompleteComp's Alt tracker are all siblings on `window`.
 *    `stopPropagation` does not stop siblings on the same target, so ordering
 *    between them never matters and the disjoint `e.code` checks keep them from
 *    overlapping. That sibling property is also what keeps AutocompleteComp's
 *    tracker observing our Alt.
 * 3. **macOS `Option+i` cannot leak a glyph.** `Option+i` is a dead key
 *    (circumflex); `preventDefault()` on keydown does not stop the text-input
 *    service from committing it, but `onTextChange` refuses any change while the
 *    Alt tracker is armed.
 *
 * `Alt+I` rather than a bare `F` (the source doc's suggestion): the utterance
 * input is permanently focused, so a bare letter is unusable. `Alt+1…9`, `Alt+A`,
 * `Alt+D` and `Alt+/` are already taken.
 *
 * `event.code` is matched so it stays layout-independent and unaffected by Alt
 * remapping the produced character.
 *
 * ## Why it swallows Alt+I even when nothing will come of it
 *
 * It calls `onFlag` unconditionally, and the two cases where that does nothing —
 * the feature flag is off, or a flag window is already open — are handled
 * downstream, in `useIssueReport` and in the `flagIssue` thunk respectively. It
 * looks like a bug that the key is still consumed in those cases, and skipping the
 * `preventDefault` / `stopPropagation` so it "falls through" looks like the fix.
 *
 * It is not. The swallow is LOAD-BEARING. `isInterceptionIgnoredEvent` ignores a
 * BARE modifier keydown but not `Alt+I` itself, and the omnibar is not on the
 * ignored-selector list, so an unswallowed Alt+I reaches
 * AgentInterceptionConfirm's document-capture listener and raises "would you like
 * to take over?" over a smoothly-driving AI. Doing nothing silently beats a
 * take-over prompt fired by a shortcut the store does not even have.
 */
const useFlagIssueHotkey = (onFlag: () => void) => {
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyI') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onFlag();
  };

  useEventListener('keydown', handleKeyDown, window, true);
};

export default useFlagIssueHotkey;
